import { supabase } from "@/lib/supabase";

const CACHE_KEY = "noa:mfa-enrollment:v1";
const API_URL = import.meta.env.VITE_SUPABASE_URL;
const FACTOR_NAME = "NOA Retraite";

interface EnrollmentCache {
  userId: string;
  apiUrl: string;
  factorId: string;
  qrCode: string | null;
  secret: string | null;
}

export type Enrollment =
  | { kind: "pending"; factorId: string; qrCode: string | null; secret: string | null }
  | { kind: "verified"; factorId: string; needsRecovery: boolean };

function readCache(): EnrollmentCache | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(CACHE_KEY) ?? "null");
    if (
      value?.apiUrl === API_URL &&
      typeof value.userId === "string" &&
      typeof value.factorId === "string" &&
      (value.qrCode === null || typeof value.qrCode === "string") &&
      (value.secret === null || typeof value.secret === "string")
    ) {
      return value;
    }
  } catch {
    // Stockage indisponible : le facteur serveur reste utilisable sans réafficher le QR.
  }
  return null;
}

function writeCache(value: EnrollmentCache) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    // Ne jamais supprimer le facteur pour compenser un stockage navigateur bloqué.
  }
}

export function clearMfaEnrollmentCache(keepUserId?: string) {
  if (keepUserId && readCache()?.userId === keepUserId) return;
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // La fermeture de l'onglet supprime aussi ce stockage temporaire.
  }
}

export function markMfaEnrollmentVerified(userId: string, factorId: string) {
  // Effacer immédiatement le secret, garder seulement le jalon des codes de secours.
  writeCache({ userId, apiUrl: API_URL, factorId, qrCode: null, secret: null });
}

function authError(code: string) {
  return Object.assign(new Error(code), { code });
}

async function listUserFactors(userId: string) {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (data.user?.id !== userId) throw authError("session_not_found");
  return (data.user.factors ?? []).filter((factor) => factor.factor_type === "totp");
}

async function prepare(userId: string, restartFactorId?: string): Promise<Enrollment> {
  clearMfaEnrollmentCache(userId);
  const cached = readCache();
  let factors = await listUserFactors(userId);
  const verified = factors.find((factor) => factor.status === "verified");
  if (verified) {
    const needsRecovery = cached?.factorId === verified.id;
    if (needsRecovery) markMfaEnrollmentVerified(userId, verified.id);
    else clearMfaEnrollmentCache();
    return { kind: "verified", factorId: verified.id, needsRecovery };
  }

  if (restartFactorId) {
    // Recommencer est une action explicite : ne supprimer que le facteur affiché,
    // jamais celui qu'un autre onglet vient de créer ni un autre type de facteur.
    const previous = factors.find((factor) => factor.id === restartFactorId);
    if (previous) {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: previous.id });
      if (error && error.code !== "mfa_factor_not_found") throw error;
    }
    clearMfaEnrollmentCache();
    factors = await listUserFactors(userId);
  }

  const pending =
    factors.find((factor) => factor.id === cached?.factorId) ??
    factors.find(
      (factor) => factor.friendly_name === FACTOR_NAME && factor.status === "unverified",
    );
  if (pending?.status === "unverified") {
    const reuse = !restartFactorId && cached?.factorId === pending.id;
    return {
      kind: "pending",
      factorId: pending.id,
      qrCode: reuse ? cached.qrCode : null,
      secret: reuse ? cached.secret : null,
    };
  }
  if (cached && !restartFactorId) {
    clearMfaEnrollmentCache();
    throw authError("mfa_factor_not_found");
  }

  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: "totp",
    issuer: FACTOR_NAME,
    friendlyName: FACTOR_NAME,
  });
  if (error) {
    // Autre onglet sans Web Locks : reprendre son facteur sans le supprimer.
    if (error.code === "mfa_factor_name_conflict") {
      const concurrent = (await listUserFactors(userId)).find(
        (factor) => factor.friendly_name === FACTOR_NAME && factor.status === "unverified",
      );
      if (concurrent) {
        return { kind: "pending", factorId: concurrent.id, qrCode: null, secret: null };
      }
    }
    throw error;
  }
  if (!data) throw authError("unexpected_failure");
  // Une déconnexion pendant l'appel ne doit pas remettre un secret dans le cache.
  const current = await supabase.auth.getSession();
  if (current.error) throw current.error;
  if (current.data.session?.user.id !== userId) throw authError("session_not_found");
  const value = {
    userId,
    apiUrl: API_URL,
    factorId: data.id,
    qrCode: data.totp.qr_code,
    secret: data.totp.secret,
  };
  writeCache(value);
  return { kind: "pending", factorId: value.factorId, qrCode: value.qrCode, secret: value.secret };
}

// Dédupliquer les montages React et sérialiser les créations entre onglets.
// Le verrou est distinct de celui utilisé par Supabase pour sa session.
const pendingPreparations = new Map<string, Promise<Enrollment>>();

export function prepareMfaEnrollment(userId: string, restartFactorId?: string) {
  const key = `${API_URL}:${userId}:${restartFactorId ?? "resume"}`;
  const running = pendingPreparations.get(key);
  if (running) return running;
  const run = () => prepare(userId, restartFactorId);
  const task = (async () => {
    if (typeof navigator !== "undefined" && navigator.locks) {
      return await navigator.locks.request(`noa:mfa-enrollment:${API_URL}:${userId}`, run);
    }
    return run();
  })().finally(() => pendingPreparations.delete(key));
  pendingPreparations.set(key, task);
  return task;
}
